import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  Download,
  Calendar,
  Users,
  Clock,
  FileText,
  TrendingUp,
  TrendingDown,
  Filter,
  RefreshCw,
  FileSpreadsheet
} from 'lucide-react';
import { format, subDays, subWeeks, subMonths, startOfWeek, endOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { useAuthStore } from '../../store/authStore';
import { useProjectStore } from '../../store/projectStore';
import { useMeetingStore } from '../../store/meetingStore';

interface ActivityStats {
  period: string;
  projects_managed: number;
  meetings_held: number;
  documents_validated: number;
  messages_sent: number;
  hours_worked: number;
  completion_rate: number;
}

interface ProjectProgress {
  project_name: string;
  owner_name: string;
  status: string;
  completion_rate: number;
  last_activity: string;
  next_deadline: string;
  risk_level: 'low' | 'medium' | 'high';
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

export const ActivityReports: React.FC = () => {
  const { user } = useAuthStore();
  const { projects } = useProjectStore();
  const { meetings } = useMeetingStore();
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter'>('month');
  const [isLoading, setIsLoading] = useState(false);
  const [activityData, setActivityData] = useState<ActivityStats[]>([]);
  const [projectProgress, setProjectProgress] = useState<ProjectProgress[]>([]);

  useEffect(() => {
    generateReportData();
  }, [timeRange, projects, meetings]);

  const generateReportData = () => {
    setIsLoading(true);
    
    // Generate mock activity data based on time range
    const periods = timeRange === 'week' ? 7 : timeRange === 'month' ? 4 : 12;
    const data: ActivityStats[] = [];
    
    for (let i = periods - 1; i >= 0; i--) {
      const date = timeRange === 'week' 
        ? subDays(new Date(), i)
        : timeRange === 'month' 
          ? subWeeks(new Date(), i)
          : subMonths(new Date(), i);
      
      data.push({
        period: timeRange === 'week' 
          ? format(date, 'EEE dd', { locale: fr })
          : timeRange === 'month'
            ? `S${format(date, 'w', { locale: fr })}`
            : format(date, 'MMM', { locale: fr }),
        projects_managed: Math.floor(Math.random() * 8) + 2,
        meetings_held: Math.floor(Math.random() * 12) + 3,
        documents_validated: Math.floor(Math.random() * 15) + 5,
        messages_sent: Math.floor(Math.random() * 25) + 10,
        hours_worked: Math.floor(Math.random() * 20) + 15,
        completion_rate: Math.floor(Math.random() * 30) + 70
      });
    }
    
    setActivityData(data);

    // Generate project progress data
    const progressData: ProjectProgress[] = projects.slice(0, 6).map(project => ({
      project_name: project.name,
      owner_name: project.owner?.full_name || 'Porteur',
      status: project.status,
      completion_rate: Math.floor(Math.random() * 40) + 60,
      last_activity: format(subDays(new Date(), Math.floor(Math.random() * 7)), 'dd/MM/yyyy'),
      next_deadline: format(new Date(Date.now() + Math.random() * 30 * 24 * 60 * 60 * 1000), 'dd/MM/yyyy'),
      risk_level: Math.random() > 0.7 ? 'high' : Math.random() > 0.4 ? 'medium' : 'low'
    }));
    
    setProjectProgress(progressData);
    setIsLoading(false);
  };

  const getTotalStats = () => {
    return activityData.reduce((acc, curr) => ({
      total_projects: acc.total_projects + curr.projects_managed,
      total_meetings: acc.total_meetings + curr.meetings_held,
      total_documents: acc.total_documents + curr.documents_validated,
      total_messages: acc.total_messages + curr.messages_sent,
      total_hours: acc.total_hours + curr.hours_worked,
      avg_completion: Math.round(activityData.reduce((sum, d) => sum + d.completion_rate, 0) / activityData.length)
    }), {
      total_projects: 0,
      total_meetings: 0,
      total_documents: 0,
      total_messages: 0,
      total_hours: 0,
      avg_completion: 0
    });
  };

  const totalStats = getTotalStats();

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRiskLabel = (risk: string) => {
    switch (risk) {
      case 'high': return 'Risque élevé';
      case 'medium': return 'Risque moyen';
      case 'low': return 'Risque faible';
      default: return 'Non évalué';
    }
  };

  const exportToCSV = () => {
    const csvRows = [];

    // Header
    csvRows.push(['Rapport d\'activité', user?.full_name, format(new Date(), 'dd/MM/yyyy à HH:mm', { locale: fr })].join(','));
    csvRows.push([]);

    // Summary stats
    csvRows.push(['Statistiques globales']);
    csvRows.push(['Projets gérés', totalStats.total_projects]);
    csvRows.push(['Rendez-vous', totalStats.total_meetings]);
    csvRows.push(['Documents validés', totalStats.total_documents]);
    csvRows.push(['Messages envoyés', totalStats.total_messages]);
    csvRows.push(['Heures travaillées', totalStats.total_hours]);
    csvRows.push(['Taux de complétion moyen', `${totalStats.avg_completion}%`]);
    csvRows.push([]);

    // Project progress
    csvRows.push(['Avancement des projets']);
    csvRows.push(['Projet', 'Porteur', 'Statut', 'Progression', 'Dernière activité', 'Prochaine échéance', 'Niveau de risque']);
    projectProgress.forEach(p => {
      csvRows.push([
        `"${p.project_name}"`,
        `"${p.owner_name}"`,
        p.status,
        `${p.completion_rate}%`,
        p.last_activity,
        p.next_deadline,
        getRiskLabel(p.risk_level)
      ].join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rapport_activite_${format(new Date(), 'yyyy-MM-dd_HHmm')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportReport = () => {
    const reportData = {
      advisor: user?.full_name,
      period: timeRange,
      generated_at: new Date().toISOString(),
      stats: totalStats,
      activity: activityData,
      projects: projectProgress
    };
    
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rapport-activite-${format(new Date(), 'yyyy-MM-dd')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const pieData = [
    { name: 'Projets gérés', value: totalStats.total_projects, color: '#3b82f6' },
    { name: 'RDV tenus', value: totalStats.total_meetings, color: '#10b981' },
    { name: 'Docs validés', value: totalStats.total_documents, color: '#f59e0b' },
    { name: 'Messages', value: totalStats.total_messages, color: '#ef4444' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rapports d'Activité</h1>
          <p className="text-gray-600 mt-1">
            Analyse de votre performance d'accompagnement
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="week">7 derniers jours</option>
            <option value="month">4 dernières semaines</option>
            <option value="quarter">3 derniers mois</option>
          </select>
          <button
            onClick={exportToCSV}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <FileSpreadsheet className="w-4 h-4" />
            <span>Exporter CSV</span>
          </button>
          <button
            onClick={exportReport}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>JSON</span>
          </button>
          <button
            onClick={generateReportData}
            disabled={isLoading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Actualiser</span>
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Projets gérés</p>
              <p className="text-3xl font-bold text-gray-900">{totalStats.total_projects}</p>
              <div className="flex items-center space-x-1 mt-1">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-600">+12%</span>
              </div>
            </div>
            <Users className="w-12 h-12 text-blue-400" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">RDV tenus</p>
              <p className="text-3xl font-bold text-gray-900">{totalStats.total_meetings}</p>
              <div className="flex items-center space-x-1 mt-1">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-600">+8%</span>
              </div>
            </div>
            <Calendar className="w-12 h-12 text-green-400" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Docs validés</p>
              <p className="text-3xl font-bold text-gray-900">{totalStats.total_documents}</p>
              <div className="flex items-center space-x-1 mt-1">
                <TrendingDown className="w-4 h-4 text-red-600" />
                <span className="text-sm text-red-600">-3%</span>
              </div>
            </div>
            <FileText className="w-12 h-12 text-orange-400" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Temps travaillé</p>
              <p className="text-3xl font-bold text-gray-900">{totalStats.total_hours}h</p>
              <div className="flex items-center space-x-1 mt-1">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-600">{Math.round(totalStats.total_hours / activityData.length)}h/période</span>
              </div>
            </div>
            <Clock className="w-12 h-12 text-purple-400" />
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activity Trend */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution de l'activité</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={activityData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="projects_managed" stroke="#3b82f6" name="Projets" />
              <Line type="monotone" dataKey="meetings_held" stroke="#10b981" name="RDV" />
              <Line type="monotone" dataKey="documents_validated" stroke="#f59e0b" name="Documents" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Activity Distribution */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition des activités</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Projects Progress Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Suivi des projets en portefeuille</h3>
          <p className="text-sm text-gray-600 mt-1">État d'avancement et risques identifiés</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Projet</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Porteur</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progression</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dernière activité</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prochaine échéance</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risque</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {projectProgress.map((project, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-900">{project.project_name}</p>
                      <p className="text-sm text-gray-600">{project.status}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-900">{project.owner_name}</p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${project.completion_rate}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600">{project.completion_rate}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-900">{project.last_activity}</p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-900">{project.next_deadline}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getRiskColor(project.risk_level)}`}>
                      {getRiskLabel(project.risk_level)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Performance Insights */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Insights de Performance</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <h4 className="font-medium text-green-900">Points forts</h4>
              </div>
              <ul className="text-sm text-green-800 space-y-1">
                <li>• Taux de completion élevé ({totalStats.avg_completion}%)</li>
                <li>• Régularité dans les rendez-vous</li>
                <li>• Bonne réactivité sur les documents</li>
              </ul>
            </div>
            
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                <h4 className="font-medium text-blue-900">Recommandations</h4>
              </div>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Planifier plus de créneaux disponibles</li>
                <li>• Utiliser plus de templates de messages</li>
                <li>• Anticiper les échéances critiques</li>
              </ul>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <TrendingDown className="w-5 h-5 text-orange-600" />
                <h4 className="font-medium text-orange-900">Points d'attention</h4>
              </div>
              <ul className="text-sm text-orange-800 space-y-1">
                <li>• {projectProgress.filter(p => p.risk_level === 'high').length} projet(s) à risque élevé</li>
                <li>• Charge de travail en augmentation</li>
                <li>• Délais serrés sur certains projets</li>
              </ul>
            </div>
            
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="w-5 h-5 text-purple-600" />
                <h4 className="font-medium text-purple-900">Efficacité</h4>
              </div>
              <ul className="text-sm text-purple-800 space-y-1">
                <li>• Temps moyen par projet : {Math.round(totalStats.total_hours / totalStats.total_projects)}h</li>
                <li>• RDV par projet : {Math.round(totalStats.total_meetings / totalStats.total_projects)}</li>
                <li>• Docs par projet : {Math.round(totalStats.total_documents / totalStats.total_projects)}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};